import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { tokenizer } from '@uhg-abyss/web/tools/tokenizer';
import { Heading } from '@uhg-abyss/web/ui/Heading';
import { Text } from '@uhg-abyss/web/ui/Text';
import { find } from 'lodash';
import React, { useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { adobeFuncErrorEvent } from '../../common/AdobeTagging/adobeFuncErrorEvent';
import { Constants } from '../../common/Constants';
import { ConstantsLagoon } from '../../common/ConstantsLagoon';
import { ContentWrapper } from '../../common/ContentWrapper';
import { PSXHeader } from '../../common/PSXHeader';
import { StillNeedHelp } from '../../common/StillNeedHelp';
import { getFeatureFlag } from '../../common/Utils';
import { isUmrMember } from '../../common/Utils/memberUtils/memberUtils';
import { PortalContext } from '../../context/PortalContext';
import { useAdobePageTrackEvent } from '../../hooks/adobeHook/useAdobePageTrackEvent';
import { useLagoon } from '../../hooks/useLagoon';
import { isGuestFlow } from '../../utils/error.utils';
import { getCurrentMember } from '../../utils/user.utils';
import { BodyWrapper, DefaultErrorWrapper, textBody } from './Page500.styled';

export const Page500 = () => {
  const { t } = useTranslation();
  const breadcrumbs = [{ title: t('ERROR_PAGE.500.title'), href: '' }];
  const { adobePageTrackEvent } = useAdobePageTrackEvent({
    is404: true,
    pageName: 'error 500',
  });

  const currentMember = getCurrentMember();
  const { getRouteParams } = useRouter();
  const { token } = getRouteParams();
  const tokenData = tokenizer.parse(token) || {};
  const { message, code, source, uiSection } = tokenData;
  const isGuest = isGuestFlow();

  const { portalData } = useContext(PortalContext);
  const isUmr = isUmrMember(portalData?.portalName);

  const uiMessaging = useLagoon(Constants.LAGOON_TABLE.UI_MESSAGING)();
  const error500Message = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.ERROR_500_MESSAGE,
  })?.message;
  const error500Message2 = find(uiMessaging, {
    key: ConstantsLagoon.UI_MESSAGING.ERROR_500_MESSAGE_2,
  })?.message;

  const featureFlags: [{ key: string; active: boolean }] =
    useLagoon('feature-flags')();

  const suppressPsxLinks: boolean = getFeatureFlag(
    featureFlags,
    ConstantsLagoon.FEATURE_FLAGS.DISABLE_PSX_LINKS_ON_MEMBER_INFO_FAILURE
  );

  useEffect(() => {
    adobeFuncErrorEvent({
      message,
      source,
      code: code ?? '500',
      uiSection,
    });
    adobePageTrackEvent();
  }, []);

  return (
    <React.Fragment>
      {!(suppressPsxLinks && !currentMember) && (
        <PSXHeader
          breadcrumbs={breadcrumbs}
          dataTestId="error-500-search-form"
          showChips={false}
          showChoosePCPHeader={false}
          showDemoHeader={false}
          showMemberSelection={false}
        />
      )}
      <DefaultErrorWrapper>
        <ContentWrapper>
          <Text
            css={{
              'abyss-text-root': {
                marginBottom: '1.25rem',
                color: '#C40000',
                fontSize: '12.64px',
                fontWeight: 'bold',
              },
            }}
          >
            {t('ERROR_PAGE.500.type')}
          </Text>
          <Heading
            css={{
              'abyss-heading-root': {
                marginBottom: '1rem',
                marginTop: '1.25rem',
              },
            }}
            level={1}
            size="lg"
          >
            {t('ERROR_PAGE.500.heading')}
          </Heading>
          <Heading
            css={{
              'abyss-heading-root': {
                marginBottom: '1.5rem',
              },
            }}
            level={6}
            size="xs"
          >
            {t('ERROR_PAGE.500.subHeading')}
          </Heading>
          {isUmr ? (
            <BodyWrapper>
              <Text fontWeight="medium">{error500Message}</Text>
              <Text fontWeight="medium">{error500Message2}</Text>
            </BodyWrapper>
          ) : (
            <BodyWrapper>
              <Text fontWeight="medium">{t('ERROR_PAGE.500.body1')}</Text>
              <Text
                css={textBody}
                dangerouslySetInnerHTML={{
                  __html: t('ERROR_PAGE.500.body2'),
                }}
                fontWeight="medium"
              />
              <Text fontWeight="medium">{t('ERROR_PAGE.500.body3')}</Text>
            </BodyWrapper>
          )}
        </ContentWrapper>
      </DefaultErrorWrapper>
      {!isGuest && <StillNeedHelp />}
    </React.Fragment>
  );
};
